// Lightweight per-language loader. Heavy bundles live in ./locales/*.
// We eagerly load Russian (default) so the first paint never waits, and
// dynamically import other languages on demand.

import type { Lang } from "./strings";
import type { SiteI18n } from "./site-types";
import ru from "./locales/ru";

export type { SiteI18n } from "./site-types";

const cache: Partial<Record<Lang, SiteI18n>> = { ru };

const loaders: Record<Lang, () => Promise<{ default: SiteI18n }>> = {
  ru: () => Promise.resolve({ default: ru }),
  ky: () => import("./locales/ky"),
  en: () => import("./locales/en"),
  uk: () => import("./locales/uk"),
  be: () => import("./locales/be"),
  fr: () => import("./locales/fr"),
  zh: () => import("./locales/zh"),
  tr: () => import("./locales/tr"),
  ko: () => import("./locales/ko"),
  de: () => import("./locales/de"),
  hi: () => import("./locales/hi"),
  ar: () => import("./locales/ar"),
  ja: () => import("./locales/ja"),
  es: () => import("./locales/es"),
};

const pending: Partial<Record<Lang, Promise<SiteI18n>>> = {};

export function ensureI18n(lang: Lang): Promise<SiteI18n> {
  if (cache[lang]) return Promise.resolve(cache[lang]!);
  if (!pending[lang]) {
    pending[lang] = loaders[lang]().then((mod) => {
      cache[lang] = mod.default;
      delete pending[lang];
      return mod.default;
    });
  }
  return pending[lang]!;
}

// Sync accessor — returns the requested language if already loaded,
// otherwise falls back to Russian (always loaded) and triggers a background load.
export function getI18n(lang: Lang): SiteI18n {
  const hit = cache[lang];
  if (hit) return hit;
  // kick off load so subsequent renders get the right language
  ensureI18n(lang).catch(() => undefined);
  return cache.en ?? ru;
}
